import React from "react";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { StrengthMeter } from "../PasswordStrength";
import { PwInput } from "./SecurityBits";
import { useChangePassword } from "./useChangePassword";

export function ChangePasswordForm({ refresh }) {
  const p = useChangePassword(refresh);
  const { form, setField, show } = p;
  return (
    <form onSubmit={p.submit} data-testid="security-change-password-form" className="px-5 py-4 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3">
      <div className="text-sm text-zinc-300">
        Change password
        <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
          Min 10 characters — a long passphrase beats a short complex one. Other browsers/devices are signed out on change.
        </p>
      </div>
      <div className="space-y-3 max-w-md">
        <PwInput id="security-current-password" label="Current password" value={form.current} onChange={setField("current")} show={show} autoComplete="current-password" />
        <div>
          <PwInput id="security-new-password" label="New password" value={form.next} onChange={setField("next")} show={show} autoComplete="new-password" />
          <StrengthMeter password={form.next} />
          {p.sameAsCurrent && <div data-testid="security-same-warning" className="text-[11px] text-amber-400 mono mt-1">must differ from the current password</div>}
        </div>
        <div>
          <PwInput id="security-confirm-password" label="Confirm new password" value={form.confirm} onChange={setField("confirm")} show={show} autoComplete="new-password" />
          {p.mismatch && <div data-testid="security-mismatch-warning" className="text-[11px] text-red-400 mono mt-1">does not match</div>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            data-testid="security-change-password-submit"
            disabled={!p.canSubmit}
            className="px-4 py-2 text-xs font-semibold bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 rounded-sm flex items-center gap-2 transition-colors"
          >
            {p.busy && <Loader2 className="animate-spin" size={12} />} Update password
          </button>
          <button
            type="button"
            data-testid="security-toggle-visibility"
            onClick={p.toggleShow}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 font-mono uppercase tracking-widest transition-colors"
          >
            {show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? "hide" : "show"}
          </button>
        </div>
        {p.err && <div data-testid="security-password-error" className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono">{p.err}</div>}
        {p.ok && (
          <div data-testid="security-password-success" className="text-xs text-emerald-300 border border-emerald-900/60 bg-emerald-950/30 px-3 py-2 rounded-sm flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{p.ok}</span>
          </div>
        )}
      </div>
    </form>
  );
}
